import { Card, Drawer, IconButton } from '@material-ui/core';
import { omit } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapRef, Marker } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { DisposalRecordFloorUnit } from '@agency/apis/disposals';
import { useOptions } from '@agency/contexts/OptionsContext';
import { CreateUpdateCompanyDrawer } from '@agency/modules/crm/pages/companies-list/components/create-update-company-drawer';
import { DisposalCapitalIncentiveCard } from '@agency/modules/disposals/pages/disposal-manage/components/disposal-record-drawer/components/disposal-record-letting/DisposalCapitalIncentiveCard';
import { DisposalHeadsOfTermsUpload } from '@agency/modules/disposals/pages/disposal-manage/components/disposal-record-drawer/components/disposal-record-letting/DisposalHeadsOfTermsUpload';
import { DisposalLeaseBreakCard } from '@agency/modules/disposals/pages/disposal-manage/components/disposal-record-drawer/components/disposal-record-letting/DisposalLeaseBreakCard';
import { DisposalRentEventCard } from '@agency/modules/disposals/pages/disposal-manage/components/disposal-record-drawer/components/disposal-record-letting/DisposalRentEventCard';
import { DisposalRentReviewCard } from '@agency/modules/disposals/pages/disposal-manage/components/disposal-record-drawer/components/disposal-record-letting/DisposalRentReviewCard';
import {
  LettingTransactionLeaseBreakStateItem,
  LettingTransactionRentReviewStateItem,
} from '@agency/modules/leases/pages/letting-manage/pages/letting-lease-events/LettingLeaseEvents.types';
import ROUTES from '@agency/routes/routes.constant';
import { usePropertiesShowQuery } from '@apis/propertiesApi';
import {
  useLettingTransactionsCreateMutation,
  useValidateDisposalLettingPublishQuery,
} from '@apis/transactionsLettingsApi';
import {
  AtlasValidationErrorResponse,
  LettingTransactionsShowItem,
  TransactionCapitalIncentiveItem,
  TransactionLeaseEventItem,
} from '@apis/transactionsLettingsApi.types';
import { useSaleTransactionsCreateMutation, useValidateDisposalSalePublishQuery } from '@apis/transactionsSalesApi';
import { SaleTransactionsShowItem } from '@apis/transactionsSalesApi.types';
import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { useChangesRecordState } from '@core/hooks/changes-state';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { addressLookupActions } from '@redux/reducers/common/address-lookup/addressLookupReducer';
import { RootState } from '@redux/reducers/rootReducer';
import { AddressLookup } from '@shared/components/address-lookup';
import { AtlasPublishError } from '@shared/components/atlas-transaction-form/AtlasPublishError';
import { AtlasPublishOptionsPanel } from '@shared/components/atlas-transaction-form/AtlasPublishOptionsPanel';
import { AtlasPublishSuccess } from '@shared/components/atlas-transaction-form/AtlasPublishSuccess';
import { Button } from '@shared/components/button-new';
import { CreateNewDrawerFooter } from '@shared/components/create-new-drawer-footer';
import { MaterialDate } from '@shared/components/date-picker';
import { DrawerHeader } from '@shared/components/drawer-header';
import { Flex } from '@shared/components/flex';
import { MultiGroupSelect } from '@shared/components/group-select/multi-group-select';
import { GroupedOption } from '@shared/components/group-select-new';
import { LettingDetailsForm, LettingDetailsFormData } from '@shared/components/letting-details-form';
import { CompanySingleLookup } from '@shared/components/lookups/company-lookup';
import { MixedUser } from '@shared/components/lookups/user-lookup';
import { ManageSection } from '@shared/components/manage-section';
import { defaultMapboxZoom, MapboxMap } from '@shared/components/mapbox-map';
import { showNotification } from '@shared/components/notification';
import { PropertiesLookup } from '@shared/components/properties-lookup';
import { AddressFormErrors } from '@shared/components/property-form';
import {
  calculateTotalSize,
  RecordSpaceItemError,
  RecordSpacesTakenForm,
  validateSpaces,
} from '@shared/components/record-spaces-taken-form';
import { SaleDetailsForm } from '@shared/components/sale-details-form';
import { Option, SingleSelectOnChange } from '@shared/components/select/Select.types';
import { SingleSelect } from '@shared/components/select/single-select';
import { StoreTransactionCard } from '@shared/components/store-transaction-card';
import { TextField } from '@shared/components/text-field';
import { AddressPayload } from '@shared/components/update-address-drawer';
import { drawerTransition } from '@shared/constants/drawer';
import { getCountryOptions } from '@shared/constants/options';
import { initialAddress, initialCountry } from '@shared/constants/transactions';
import { PageViewState, usePageState } from '@shared/hooks/viewState';
import { MarkerIcon } from '@shared/icons/marker/Marker';
import { PlusIcon } from '@shared/icons/plus';
import { RequirementDocumentImage } from '@shared/icons/requirement-document';
import { MixedCompany } from '@shared/models/company/company';
import { Contact } from '@shared/models/contact/contact';
import { LettingTransactionTypes } from '@shared/models/transactions/lettings/lettings';
import { TransactionType } from '@shared/models/transactions/transactions';
import { ButtonSize, NewButtonColour } from '@shared/types/common/button';
import { EventTypes, LookupUserItem } from '@shared/types/common/common';
import { InputSize } from '@shared/types/common/input';
import { MapPosition } from '@shared/types/common/map';
import { formatFullAddress } from '@shared/utils/address';
import {
  getLettingsAtlasPublishFields,
  getSalesAtlasPublishFieldsInvestment,
  getSalesAtlasPublishFieldsOccupational,
} from '@shared/utils/atlas-publish-fields';
import { getDefaultError, someStringRecord } from '@shared/utils/common';
import { configUserToLookupUser } from '@shared/utils/lookup';

import { AgencyRoleSection } from './components/agency-role-section';
import { styles } from './CreateTransactionDrawer.styles';
import { serialiseLettingTransaction, serialiseSaleTransaction } from './CreateTransactionDrawer.utils';
import { CreateTransactionSteps, CreateTransactionTypes } from './types';

export interface CreateTransactionDrawerProps extends WithStyles<typeof styles> {
  createType: CreateTransactionTypes | null;
  open: boolean;
  propertyId?: string;
  tagGroupOptions: GroupedOption<number>[];
  type: TransactionType;
  onClose: () => void;
}

export interface LeaseCardObjectInterface {
  leaseEnd: MaterialDate;
  leaseEndReadable: string;
  leaseLengthDays: string;
  leaseLengthMonths: string;
  leaseLengthYears: string;
  leaseStart: MaterialDate;
  ourAcquisition: boolean;
  ourDisposal: boolean;
  rent: string;
  rentCurrency: string;
  rentMetric: Metric;
  rentRates: string;
  transactionDate: MaterialDate;
  transactionType: number;
}

export interface SpaceCardObjectInterface {
  measure: string;
  size: string;
  sizeMetric: Metric;
}

type CompanyCreationField = 'company' | 'vendor' | 'landlord';

export interface CreateTransactionFormData {
  // Address & Property
  address: Address;
  countrySelectValue: Option<string> | null;
  mapPosition: MapPosition | undefined;
  searchAddressInput: string;
  enterManuallyMode: boolean;
  existingPropertyId: number | undefined;
  exactMatch: boolean;

  // Transaction Core
  selectedTagIds: number[];
  assignee: LookupUserItem | undefined;

  // Space Details
  entireProperty: number;
  floorUnits: DisposalRecordFloorUnit[];
  propertyDetailsRadio: Id;
  sizeValue: string;
  sizeMetric: Metric;
  sizeMeasure: string;

  // Lease Details (Lettings) - flattened from leaseCardObject
  leaseEnd: MaterialDate;
  leaseEndReadable: string;
  leaseLengthDays: string;
  leaseLengthMonths: string;
  leaseLengthYears: string;
  leaseStart: MaterialDate;
  rent: string;
  rentCurrency: string;
  rentMetric: Metric;
  rentRates: string;
  transactionDate: MaterialDate;
  transactionType: number;

  // Headline Details (Sales) - flattened from headlineDetailsCardObject
  capital: string;
  capitalCurrency: string;
  leaseComment: string;
  leaseExpiryMonth: number;
  leaseExpiryYear: string;
  purchasePrice: string;
  purchasePriceCurrency: string;
  saleType: number | string;

  // Our Acquisition/Disposal (shared between lettings and sales)
  ourAcquisition: boolean;
  ourDisposal: boolean;

  // Tenant/Purchaser
  tenantRadio: string;
  company: MixedCompany | undefined;
  contact: Contact | undefined;
  landlord: MixedCompany | undefined;
  vendor: MixedCompany | undefined;

  // Events
  rentFreeEvents: TransactionLeaseEventItem[];
  leaseBreaks: LettingTransactionLeaseBreakStateItem[];
  rentReviews: LettingTransactionRentReviewStateItem[];
  capitalIncentives: TransactionCapitalIncentiveItem[];

  // Additional Info
  buildingTypes: string[];
  headsOfTerms: File | null;
  transactionNotes: string;
  sectors: string[];
  source: string;
  netInitialYield: string;
  passingRent: string;
  passingRentCurrency: string;
  passingRentMetric: string;
  waultToBreak: string;
  waultToExpiry: string;

  // Agency Role
  agencyRole: string;
  landlordAgents: MixedUser[];
  tenantAgents: MixedUser[];

  // Validation flags
  floorCardError: boolean;
  sizeError: boolean;
}

export interface ValidationErrors {
  error: string;
  addressFormErrors: AddressFormErrors;
  atlasValidationErrors: AtlasValidationErrorResponse | null;
  buildingTypesError: boolean;
  leaseBreaksErrors: Record<string, boolean>;
  ourDisposalError: boolean;
  purchasePriceError: boolean;
  rentError: boolean;
  rentFreeEventsErrors: Record<string, boolean>;
  rentReviewsErrors: Record<string, boolean>;
  spaceCardErrors: Record<number, RecordSpaceItemError>;
  transactionDateError: boolean;
}

interface ApiValidationErrorResponse {
  message?: string;
  errors?: {
    mandatory_errors?: Array<{ message?: string }>;
    building_types?: unknown;
    signature_date?: unknown;
    completion_date?: unknown;
    our_disposal?: unknown;
    [key: string]: unknown;
  };
}

interface ApiErrorResponse {
  response?: {
    status?: number;
    data?: ApiValidationErrorResponse;
  };
}

type AtlasPublishPreviewLetting = Pick<
  LettingTransactionsShowItem,
  'id' | 'primary_address' | 'secondary_address' | 'images' | 'size_value' | 'size_metric' | 'building_types'
> & {
  rent_currency: string;
  rent_pa: number | null;
  rent_psf: null;
  lease_start: string | null;
  lease_end: string | null;
  signature_date: string | null;
};

type AtlasPublishPreviewSale = Pick<
  SaleTransactionsShowItem,
  'id' | 'primary_address' | 'secondary_address' | 'images' | 'size_value' | 'size_metric' | 'building_types'
> & {
  purchase_price: number;
  purchase_price_currency: string;
  completion_date: string | null;
  passing_rent: number | null;
  passing_rent_currency: string;
  passing_rent_metric: string | null;
  passing_niy: number;
  wault_to_break: number;
  wault_to_expiry: number;
};

const defaultValidationErrors: ValidationErrors = {
  error: '',
  addressFormErrors: {
    line1: '',
    line1Warning: '',
    nameWarning: '',
    postcode: '',
  },
  atlasValidationErrors: null,
  buildingTypesError: false,
  leaseBreaksErrors: {},
  ourDisposalError: false,
  purchasePriceError: false,
  rentError: false,
  rentFreeEventsErrors: {},
  rentReviewsErrors: {},
  spaceCardErrors: {},
  transactionDateError: false,
};

const generateRentEventId = () => {
  const postfix = `event-id-${Math.random().toString(36).slice(2, 11)}`;
  return `rent_free_events-${postfix}`;
};

const createInitialRentEvent = (): TransactionLeaseEventItem => ({
  break_option: '',
  break_penalty_comments: '',
  break_penalty_currency: '',
  break_penalty: '',
  comments: '',
  confirmed: false,
  date_days: null,
  date_months: null,
  date_years: null,
  date_priority: null,
  from: null,
  id: generateRentEventId(),
  notice_months: null,
  organisation_id: 0,
  reminder_days: -1,
  rent_currency: '',
  rent_metric: null,
  rent_value: null,
  to: null,
  type: EventTypes.LeaseRentFree,
  upward_only: false,
});

const generateLeaseBreakId = () => {
  const postfix = `lease-break-id-${Math.random().toString(36).slice(2, 11)}`;
  return `temp-${postfix}`;
};

const createInitialLeaseBreak = (): LettingTransactionLeaseBreakStateItem => ({
  break_option: '',
  break_penalty_comments: '',
  break_penalty: '',
  comments: '',
  date_days: null,
  date_months: null,
  date_years: null,
  date_priority: null,
  from: null,
  id: generateLeaseBreakId(),
  notice_months: '',
  reminder_days: -1,
});

const generateRentReviewId = () => {
  const postfix = `rent-review-id-${Math.random().toString(36).slice(2, 11)}`;
  return `temp-${postfix}`;
};

const createInitialRentReview = (): LettingTransactionRentReviewStateItem => ({
  comments: '',
  date_days: null,
  date_months: null,
  date_years: null,
  date_priority: null,
  from: null,
  id: generateRentReviewId(),
  reminder_days: -1,
  upward_only: false,
});

const createInitialCapitalIncentive = (): TransactionCapitalIncentiveItem => ({
  amount: null,
  comments: null,
});

const isCreateTransactionType = (value: CreateTransactionTypes | null): value is CreateTransactionTypes => {
  return value !== null;
};

const CreateTransactionDrawerComponent: React.FC<CreateTransactionDrawerProps> = ({
  classes,
  createType: suppliedCreateType,
  open,
  propertyId,
  tagGroupOptions,
  type,
  onClose,
}) => {
  const dispatch = useDispatch();
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();

  const { coogan, user, featuresEnabled } = useConfig();
  const { constants } = useAgencyConstants();
  const { availableLettingTypeOptions, fitoutConditionOptions } = useOptions();

  const hasAtlasPermissions = useMemo(() => {
    return Boolean(featuresEnabled?.webapp?.atlas_publish);
  }, [featuresEnabled?.webapp?.atlas_publish]);

  const { addressAutocomplete, addressDoctorAutocomplete, coords, coordsError, coordsLoading } = useSelector(
    (state: RootState) => state.common?.addressLookup
  );

  const defaultMapPosition: MapPosition = useMemo(
    () => ({ lat: constants.localisation.lat, lng: constants.localisation.lng, zoom: defaultMapboxZoom }),
    [constants]
  );

  const lettingsFieldList = useMemo(() => getLettingsAtlasPublishFields(), []);
  const salesFieldInvestment = useMemo(() => getSalesAtlasPublishFieldsInvestment(), []);
  const salesFieldOccupational = useMemo(() => getSalesAtlasPublishFieldsOccupational(), []);

  const initialFormData = useMemo<CreateTransactionFormData>(
    () => ({
      // Address & Property
      address: { ...initialAddress },
      countrySelectValue: initialCountry,
      mapPosition: undefined,
      searchAddressInput: '',
      enterManuallyMode: false,
      existingPropertyId: propertyId ? Number(propertyId) : undefined,
      exactMatch: false,

      // Transaction Core
      selectedTagIds: [],
      assignee: undefined,

      // Space Details
      entireProperty: 1,
      floorUnits: [],
      propertyDetailsRadio: 'entireProperty',
      sizeValue: '',
      sizeMetric: constants.defaultSizeMetric || '',
      sizeMeasure: '',

      // Lease Details (Lettings) - flattened
      leaseEnd: '',
      leaseEndReadable: '',
      leaseLengthDays: '',
      leaseLengthMonths: '',
      leaseLengthYears: '',
      leaseStart: null,
      rent: '',
      rentCurrency: constants.localisation.currencyDefaultIso ?? '',
      rentMetric: constants.defaultRentMetric || '',
      rentRates: 'exclusive',
      transactionDate: moment().format('YYYY-MM-DD'),
      transactionType: 0,

      // Headline Details (Sales) - flattened
      capital: '',
      capitalCurrency: constants.localisation.currencyDefaultIso,
      leaseComment: '',
      leaseExpiryMonth: 0,
      leaseExpiryYear: '',
      purchasePrice: '',
      purchasePriceCurrency: constants.localisation.currencyDefaultIso,
      saleType: '',

      // Our Acquisition/Disposal (shared)
      ourAcquisition: false,
      ourDisposal: false,

      // Tenant/Purchaser
      tenantRadio: '1',
      company: undefined,
      contact: undefined,
      landlord: undefined,
      vendor: undefined,

      // Events
      rentFreeEvents: [],
      leaseBreaks: [],
      rentReviews: [],
      capitalIncentives: [],

      // Additional Info
      buildingTypes: [],
      headsOfTerms: null,
      transactionNotes: '',
      sectors: [],
      source: '',
      netInitialYield: '',
      passingRent: '',
      passingRentCurrency: '',
      passingRentMetric: '',
      waultToBreak: '',
      waultToExpiry: '',

      // Agency Role
      agencyRole: '',
      landlordAgents: [],
      tenantAgents: [],

      // Validation flags
      floorCardError: false,
      sizeError: false,
    }),
    [constants, propertyId]
  );

  // Separate state for UI flow (not part of form data)
  const [step, setStep] = useState<CreateTransactionSteps>(
    suppliedCreateType !== null ? CreateTransactionSteps.ADDRESS_LOOKUP : CreateTransactionSteps.SELECT_TYPE
  );
  const [createType, setCreateType] = useState<CreateTransactionDrawerProps['createType']>(suppliedCreateType);

  // Form state management
  const formChanges = useChangesRecordState<CreateTransactionFormData>(initialFormData);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(defaultValidationErrors);

  // Section collapse states
  const [incentivesExpanded, setIncentivesExpanded] = useState(false);
  const [additionalInfoExpanded, setAdditionalInfoExpanded] = useState(false);

  // Company creation state
  const [createCompanyName, setCreateCompanyName] = useState<string | null>(null);
  const [createCompanyField, setCreateCompanyField] = useState<CompanyCreationField>('company');

  // Page state for publish flow
  const getInitialPageState = () => ({
    publish_error: '',
    publish_options: '',
    publish_success: '',
  });
  type CreateTransactionPageState = PageViewState<ReturnType<typeof getInitialPageState>>;
  const { pageState, updatePageState } = usePageState<CreateTransactionPageState>(() => getInitialPageState());
  const [savedTransactionId, setSavedTransactionId] = useState<string>('');
  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>({});

  const useAddressDoctor = useMemo(() => user.organisation?.settings?.address_doctor === '1', [user.organisation]);
  const useLoqate = useMemo(() => !useAddressDoctor, [useAddressDoctor]);

  const countryOptions = useMemo(() => getCountryOptions(constants), [constants]);

  const isUkRegion = useMemo(() => constants.localisation.region === 'UK', [constants]);

  const isUprnEditable = useMemo(
    () => (useAddressDoctor && isUkRegion) || coogan,
    [useAddressDoctor, isUkRegion, coogan]
  );

  const showInternalUseText = useMemo(() => !useAddressDoctor && coogan, [useAddressDoctor, coogan]);

  const getCoords = (_address: Address) => {
    dispatch(addressLookupActions.getCoordsFromAddressRequest(_address));
  };

  const centreMap = useCallback(() => {
    if (!mapRef || !formChanges.state.mapPosition) {
      return;
    }

    mapRef.current?.flyTo({
      center: { lat: formChanges.state.mapPosition.lat, lng: formChanges.state.mapPosition.lng },
      zoom: formChanges.state.mapPosition.zoom || defaultMapboxZoom,
    });
  }, [formChanges.state.mapPosition, mapRef]);

  const formattedFullPropertyAddress = useMemo(() => {
    return formatFullAddress(formChanges.state.address);
  }, [formChanges.state.address]);

  useEffect(() => {
    if (coords && formChanges.state.mapPosition) {
      formChanges.updateState({
        mapPosition: { ...formChanges.state.mapPosition, lat: coords.lat, lng: coords.lng },
      });
    }
  }, [coords]);

  useEffect(() => {
    centreMap();
  }, [formChanges.state.mapPosition?.lat, formChanges.state.mapPosition?.lng, centreMap]);

  // PCA / Loqate
  useEffect(() => {
    if (!useLoqate || !addressAutocomplete.fullAddress) {
      return;
    }

    const countryOption = countryOptions.filter(({ id }) => id == addressAutocomplete.fullAddress?.CountryIso2)[0];
    const countryValue = countryOption || initialCountry;

    formChanges.updateState({
      address: {
        name: '',
        line1: addressAutocomplete.fullAddress.Line1,
        line2: addressAutocomplete.fullAddress.Line2,
        town: addressAutocomplete.fullAddress.City,
        county: addressAutocomplete.fullAddress.ProvinceName,
        postcode: addressAutocomplete.fullAddress.PostalCode,
        country: countryValue.label,
        domestic_id: addressAutocomplete.fullAddress.DomesticId,
      },
      countrySelectValue: countryValue,
      mapPosition: {
        lat: parseFloat(addressAutocomplete.fullAddress.Field1) || constants.localisation.lat,
        lng: parseFloat(addressAutocomplete.fullAddress.Field2) || constants.localisation.lng,
        zoom: defaultMapboxZoom,
      },
    });
  }, [addressAutocomplete.fullAddress, useLoqate, countryOptions]);

  // Address Doctor
  useEffect(() => {
    if (!useAddressDoctor || !addressDoctorAutocomplete?.fullAddress) {
      return;
    }

    const {
      building = null,
      house_number = '',
      street = '',
      line2_extra = '',
      town = '',
      county = '',
      postcode = '',
      country = '',
    } = addressDoctorAutocomplete.fullAddress;

    // Build the street address
    const streetAddress = [house_number, street].filter(Boolean).join(' ');

    // Determine line1 and line2 based on available data
    const [line1, line2] = building ? [building || '', streetAddress] : [streetAddress, line2_extra || ''];

    const countryOption = countryOptions.find(({ id }) => id === country) || initialCountry;

    const _address: Address = {
      name: '',
      line1: line1 || '',
      line2: line2 || '',
      town: town || '',
      county: county || '',
      postcode: postcode || '',
      country: countryOption.label,
      domestic_id: addressDoctorAutocomplete?.fullAddressUPRN?.toString() || '',
    };

    formChanges.updateState({
      address: _address,
      countrySelectValue: countryOption,
    });

    // Fetch coordinates since Address Doctor doesn't return them
    getCoords(_address);
  }, [
    addressDoctorAutocomplete.fullAddress,
    addressDoctorAutocomplete?.fullAddressUPRN,
    useAddressDoctor,
    countryOptions,
  ]);

  usePropertiesShowQuery(propertyId || '', {
    enabled: !!propertyId,
    onSuccess: (data) => {
      formChanges.updateState({
        address: { ...formChanges.state.address, ...data.address },
        mapPosition: {
          ...defaultMapPosition,
          lat: data.lat || constants.localisation.lat,
          lng: data.lng || constants.localisation.lng,
        },
        existingPropertyId: Number(propertyId),
      });

      setCreateType(suppliedCreateType);
    },
    onError: () => {
      showNotification(getDefaultError(), 'error');
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      setCreateType(suppliedCreateType);

      if (propertyId) {
        setStep(CreateTransactionSteps.ADDRESS_CONFIRM);
      } else if (suppliedCreateType !== null) {
        setStep(CreateTransactionSteps.ADDRESS_LOOKUP);
      } else {
        setStep(CreateTransactionSteps.SELECT_TYPE);
      }
    } else {
      // Clear all form data
      formChanges.resetState(initialFormData);
      setValidationErrors(defaultValidationErrors);
      setIncentivesExpanded(false);
      setAdditionalInfoExpanded(false);
    }
  }, [open, initialFormData]);

  useEffect(() => {
    if (step == CreateTransactionSteps.ADDRESS_LOOKUP) {
      formChanges.updateState({
        address: { ...initialAddress },
        countrySelectValue: initialCountry,
        searchAddressInput: '',
        mapPosition: { ...defaultMapPosition },
        existingPropertyId: undefined,
        enterManuallyMode: false,
      });
    }

    if (step == CreateTransactionSteps.ADDRESS_CONFIRM) {
      formChanges.updateState({
        assignee: configUserToLookupUser(user),
      });
    }
  }, [step, initialAddress, defaultMapPosition, user]);

  useEffect(() => {
    if (formChanges.state.floorCardError) {
      formChanges.updateState({ floorCardError: false });
    }
    setValidationErrors((prev) => ({ ...prev, error: '' }));
  }, [formChanges.state.floorUnits]);

  useEffect(() => {
    const floorUnitPropertyTypes = formChanges.state.floorUnits
      .flatMap((unit) => unit.property_types || [])
      .filter(Boolean);

    if (floorUnitPropertyTypes.length > 0) {
      const currentBuildingTypes = formChanges.state.buildingTypes || [];
      const aggregated = Array.from(new Set([...currentBuildingTypes, ...floorUnitPropertyTypes]));

      if (JSON.stringify(aggregated.sort()) !== JSON.stringify(currentBuildingTypes.sort())) {
        formChanges.updateState({ buildingTypes: aggregated });
      }
    }
  }, [formChanges.state.floorUnits]);

  useEffect(() => {
    setValidationErrors((prev) => ({ ...prev, error: '' }));
  }, [formChanges.state.address.line1, formChanges.state.address.postcode, step]);

  useEffect(() => {
    if (validationErrors.buildingTypesError && formChanges.state.buildingTypes.length > 0) {
      setValidationErrors((prev) => ({
        ...prev,
        buildingTypesError: false,
      }));
    }
  }, [formChanges.state.buildingTypes, validationErrors.buildingTypesError]);

  useEffect(() => {
    if (validationErrors.ourDisposalError && formChanges.state.ourDisposal === true) {
      setValidationErrors((prev) => ({
        ...prev,
        ourDisposalError: false,
      }));
    }
  }, [formChanges.state.ourDisposal, validationErrors.ourDisposalError]);

  useEffect(() => {
    if (validationErrors.transactionDateError) {
      if (formChanges.state.transactionDate) {
        setValidationErrors((prev) => ({
          ...prev,
          transactionDateError: false,
        }));
      }
    }
  }, [formChanges.state.transactionDate, formChanges.state.transactionDate, validationErrors.transactionDateError]);

  useEffect(() => {
    if (validationErrors.rentError && formChanges.state.rent) {
      setValidationErrors((prev) => ({
        ...prev,
        rentError: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formChanges.state.rent]);

  useEffect(() => {
    if (validationErrors.purchasePriceError && formChanges.state.purchasePrice) {
      setValidationErrors((prev) => ({
        ...prev,
        purchasePriceError: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formChanges.state.purchasePrice]);

  useEffect(() => {
    if (
      !validationErrors.buildingTypesError &&
      !validationErrors.transactionDateError &&
      !validationErrors.ourDisposalError &&
      !validationErrors.rentError &&
      !validationErrors.purchasePriceError &&
      validationErrors.error &&
      validationErrors.atlasValidationErrors
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        error: '',
        atlasValidationErrors: null,
      }));
    }
  }, [
    validationErrors.buildingTypesError,
    validationErrors.transactionDateError,
    validationErrors.ourDisposalError,
    validationErrors.rentError,
    validationErrors.purchasePriceError,
    validationErrors.error,
    validationErrors.atlasValidationErrors,
  ]);

  const headerTitle = useMemo(() => {
    switch (createType) {
      case CreateTransactionTypes.LEASE:
        return t('store_a_lease_transaction');

      case CreateTransactionTypes.LEASE_RENEWAL:
        return t('store_a_lease_renewal_transaction');

      case CreateTransactionTypes.RENT_REVIEW:
        return t('store_a_rent_review_transaction');

      case CreateTransactionTypes.OCCUPATIONAL_SALE:
        return t('store_an_occupational_sale_transaction');

      case CreateTransactionTypes.INVESTMENT_SALE:
        return t('store_an_investment_sale_transaction');

      default:
        return t('store_a_transaction');
    }
  }, [createType]);

  const addressFormErrors: AddressFormErrors = useMemo(() => {
    let propertyNameWarning = '';
    let line1Error = '';
    let line1Warning = '';
    let postCodeError = '';

    if (
      formChanges.state.address.name &&
      formChanges.state.address.name
        .trim()
        .split(' ')
        .some((el) => {
          return !isNaN(Number(el)) || el.toLowerCase().includes(t('floor').toLowerCase());
        })
    ) {
      propertyNameWarning = t(
        'it_looks_like_this_might_be_address_line_1_this_field_is_for_the_building_name_or_unit_name_for_example_eagle_house'
      );
    }

    if (formChanges.state.address.line1) {
      if (formChanges.state.address.line1.length < 3) {
        line1Error = t('too_short_address');
      } else if (
        formChanges.state.address.line1
          .split(' ')
          .some((el: string) => el.toLowerCase().includes(t('floor').toLowerCase()))
      ) {
        line1Warning = t(
          'it_looks_like_youre_trying_to_add_floors_or_units_here_you_can_also_define_the_floors_and_spaces_on_the_availability_page_once_created'
        );
      }
    } else {
      line1Error = t('xtextx_is_required', { text: t('address_line_1_1') });
    }

    if (isUkRegion) {
      if (!formChanges.state.address.postcode) {
        postCodeError = t('xtextx_is_required', { text: t('postcode') });
      } else if (formChanges.state.address.postcode.length < 3) {
        postCodeError = t('too_short_postcode');
      }
    }

    return {
      line1: line1Error,
      line1Warning: line1Warning,
      nameWarning: propertyNameWarning,
      postcode: postCodeError,
    };
  }, [formChanges.state.address, isUkRegion]);

  const calculatedTotalSize = useMemo(() => {
    const totalSize = calculateTotalSize(
      formChanges.state.floorUnits.filter((unit) => unit.include && !!unit.size_value),
      formChanges.state.sizeMetric,
      0,
      0
    );

    return totalSize > 0 ? totalSize.toString() : '';
  }, [formChanges.state.floorUnits, formChanges.state.sizeMetric]);

  useEffect(() => {
    formChanges.updateState({ sizeValue: calculatedTotalSize });
  }, [calculatedTotalSize]);

  const thirdStepHandle = () => {
    setStep(CreateTransactionSteps.ADDRESS_CONFIRM);
    formChanges.updateState({ enterManuallyMode: true });
  };

  const fourthStepHandle = () => {
    setStep(CreateTransactionSteps.SET_DETAILS);
  };

  const handleSkipPropertyLookupStep = (exactMatchId?: number) => {
    if (exactMatchId) {
      formChanges.updateState({ exactMatch: true });
    }

    fourthStepHandle();
  };

  const thirdHandleStep = () => {
    setStep(CreateTransactionSteps.PROPERTIES_LOOKUP);
  };

  const handleSaveAndSetSpaceDetails = useCallback(() => {
    const hasError = someStringRecord(omit(addressFormErrors, ['nameWarning', 'line1Warning']), (value) => !!value);

    let errorValue = '';
    if (hasError) {
      errorValue = t('please_fix_the_errors_above_and_try_again');
    } else if (
      formChanges.state.mapPosition?.lat == constants.localisation.lat &&
      formChanges.state.mapPosition?.lng == constants.localisation.lng
    ) {
      errorValue = t('pick_a_location_on_the_map');
    }

    setValidationErrors((prev) => ({ ...prev, error: errorValue }));

    if (!errorValue) {
      thirdHandleStep();
    }
  }, [addressFormErrors, constants.localisation, formChanges.state.mapPosition]);

  const handleSelectCountry: SingleSelectOnChange = (e, value, option) => {
    if (option) {
      formChanges.updateState({
        address: { ...formChanges.state.address, country: option.label },
        countrySelectValue: option as Option<string>,
      });
    }
  };

  const prevStep = useMemo(() => {
    let prevToDetailsStep = CreateTransactionSteps.PROPERTIES_LOOKUP;

    if (formChanges.state.exactMatch && formChanges.state.existingPropertyId) {
      prevToDetailsStep = CreateTransactionSteps.ADDRESS_CONFIRM;
    }

    if (!formChanges.state.existingPropertyId) {
      prevToDetailsStep = CreateTransactionSteps.ADDRESS_CONFIRM;
    }

    const configStep = {
      [CreateTransactionSteps.SELECT_TYPE]: CreateTransactionSteps.SELECT_TYPE,
      [CreateTransactionSteps.ADDRESS_LOOKUP]: CreateTransactionSteps.SELECT_TYPE,
      [CreateTransactionSteps.ADDRESS_CONFIRM]: CreateTransactionSteps.ADDRESS_LOOKUP,
      [CreateTransactionSteps.SET_DETAILS]: prevToDetailsStep,
      [CreateTransactionSteps.PROPERTIES_LOOKUP]: CreateTransactionSteps.ADDRESS_CONFIRM,
    };

    return configStep[step];
  }, [step, formChanges.state.exactMatch, formChanges.state.existingPropertyId]);

  const returnToPreviousStep = () => {
    if (step === CreateTransactionSteps.ADDRESS_LOOKUP) {
      setCreateType(null);
    } else if (step === CreateTransactionSteps.ADDRESS_CONFIRM) {
      clearAddressesList();
      formChanges.updateState({
        enterManuallyMode: false,
        floorUnits: [],
        propertyDetailsRadio: 'entireProperty',
        // Reset space details
        sizeValue: '',
        sizeMetric: constants.defaultSizeMetric || '',
        sizeMeasure: '',
        // Reset lease details
        leaseEnd: '',
        leaseEndReadable: '',
        leaseLengthDays: '',
        leaseLengthMonths: '',
        leaseLengthYears: '',
        leaseStart: null,
        rent: '',
        rentCurrency: constants.localisation.currencyDefaultIso ?? '',
        rentMetric: constants.defaultRentMetric || '',
        rentRates: 'exclusive',
        transactionDate: moment().format('YYYY-MM-DD'),
        transactionType: 0,
        // Reset headline details
        capital: '',
        capitalCurrency: constants.localisation.currencyDefaultIso,
        leaseComment: '',
        leaseExpiryMonth: 0,
        leaseExpiryYear: '',
        purchasePrice: '',
        purchasePriceCurrency: constants.localisation.currencyDefaultIso,
        saleType: 0,
        // Reset our acquisition/disposal
        ourAcquisition: false,
        ourDisposal: false,
      });
    } else if (step === CreateTransactionSteps.PROPERTIES_LOOKUP) {
      formChanges.updateState({ existingPropertyId: undefined });
    } else if (step === CreateTransactionSteps.SET_DETAILS) {
      if (formChanges.state.exactMatch && formChanges.state.existingPropertyId) {
        formChanges.updateState({
          exactMatch: false,
          existingPropertyId: undefined,
        });
      }
    }

    setStep(prevStep);
  };

  const clearAddressesList = () => {
    dispatch(addressLookupActions.setAutocompletePreviewAddress([]));
    dispatch(addressLookupActions.setAutocompleteFinalAddress(undefined));
  };

  const headlineVisible = useMemo(
    () =>
      createType === CreateTransactionTypes.OCCUPATIONAL_SALE || createType === CreateTransactionTypes.INVESTMENT_SALE,
    [createType]
  );

  const selectTransactionType = (type: number) => {
    setCreateType(type);
    setStep(CreateTransactionSteps.ADDRESS_LOOKUP);
  };

  // Sync transactionType with createType for letting transactions
  useEffect(() => {
    if (createType !== null && hasAtlasPermissions) {
      let lettingType: number | null = null;

      if (createType === CreateTransactionTypes.LEASE) {
        lettingType = LettingTransactionTypes.NEW_LEASE; // 0 - "New Lease"
      } else if (createType === CreateTransactionTypes.LEASE_RENEWAL) {
        lettingType = LettingTransactionTypes.RENEWAL; // 2 - "Renewal"
      } else if (createType === CreateTransactionTypes.RENT_REVIEW) {
        lettingType = LettingTransactionTypes.RENT_REVIEW; // 3 - "Rent Review"
      }

      if (lettingType !== null && formChanges.state.transactionType !== lettingType) {
        formChanges.updateState({ transactionType: lettingType });
      }
    }
  }, [createType, hasAtlasPermissions]);

  const handleAddRentEvent = () => {
    const newEvent = createInitialRentEvent();
    formChanges.updateState({
      rentFreeEvents: [...formChanges.state.rentFreeEvents, newEvent],
    });
  };

  const handleUpdateRentEvent = (updatedEvent: TransactionLeaseEventItem) => {
    const updatedEvents = formChanges.state.rentFreeEvents.map((event) =>
      event.id === updatedEvent.id ? updatedEvent : event
    );
    formChanges.updateState({ rentFreeEvents: updatedEvents });
  };

  const handleDeleteRentEvent = (eventId: string | number) => {
    const updatedEvents = formChanges.state.rentFreeEvents.filter((event) => event.id !== eventId);
    const { [eventId]: _, ...remainingErrors } = validationErrors.rentFreeEventsErrors;
    formChanges.updateState({ rentFreeEvents: updatedEvents });
    setValidationErrors((prev) => ({ ...prev, rentFreeEventsErrors: remainingErrors }));
  };

  const handleAddLeaseBreak = () => {
    const newLeaseBreak = createInitialLeaseBreak();
    formChanges.updateState({
      leaseBreaks: [...formChanges.state.leaseBreaks, newLeaseBreak],
    });
  };

  const handleUpdateLeaseBreak = (
    leaseBreakId: number | string | null,
    updates: Partial<LettingTransactionLeaseBreakStateItem>
  ) => {
    const updatedLeaseBreaks = formChanges.state.leaseBreaks.map((leaseBreak) =>
      leaseBreak.id === leaseBreakId ? { ...leaseBreak, ...updates } : leaseBreak
    );
    formChanges.updateState({ leaseBreaks: updatedLeaseBreaks });
  };

  const handleDeleteLeaseBreak = (leaseBreakId: number | string | null) => {
    const updatedLeaseBreaks = formChanges.state.leaseBreaks.filter((leaseBreak) => leaseBreak.id !== leaseBreakId);
    const { [leaseBreakId?.toString() ?? '']: _, ...remainingErrors } = validationErrors.leaseBreaksErrors;
    formChanges.updateState({ leaseBreaks: updatedLeaseBreaks });
    setValidationErrors((prev) => ({ ...prev, leaseBreaksErrors: remainingErrors }));
  };

  const handleAddRentReview = () => {
    const newRentReview = createInitialRentReview();
    formChanges.updateState({
      rentReviews: [...formChanges.state.rentReviews, newRentReview],
    });
  };

  const handleUpdateRentReview = (
    rentReviewId: number | string | null,
    updates: Partial<LettingTransactionRentReviewStateItem>
  ) => {
    const updatedRentReviews = formChanges.state.rentReviews.map((rentReview) =>
      rentReview.id === rentReviewId ? { ...rentReview, ...updates } : rentReview
    );
    formChanges.updateState({ rentReviews: updatedRentReviews });
  };

  const handleDeleteRentReview = (rentReviewId: number | string | null) => {
    const updatedRentReviews = formChanges.state.rentReviews.filter((rentReview) => rentReview.id !== rentReviewId);
    const { [rentReviewId?.toString() ?? '']: _, ...remainingErrors } = validationErrors.rentReviewsErrors;
    formChanges.updateState({ rentReviews: updatedRentReviews });
    setValidationErrors((prev) => ({ ...prev, rentReviewsErrors: remainingErrors }));
  };

  const handleAddCapitalIncentive = () => {
    const newIncentive = createInitialCapitalIncentive();
    formChanges.updateState({
      capitalIncentives: [...formChanges.state.capitalIncentives, newIncentive],
    });
  };

  const handleUpdateCapitalIncentive = (index: number, updates: Partial<TransactionCapitalIncentiveItem>) => {
    const updatedIncentives = formChanges.state.capitalIncentives.map((incentive, idx) =>
      idx === index ? { ...incentive, ...updates } : incentive
    );
    formChanges.updateState({ capitalIncentives: updatedIncentives });
  };

  const handleDeleteCapitalIncentive = (index: number) => {
    const updatedIncentives = formChanges.state.capitalIncentives.filter((_, idx) => idx !== index);
    formChanges.updateState({ capitalIncentives: updatedIncentives });
  };

  const handleUploadFile = (file: File) => {
    formChanges.updateState({ headsOfTerms: file });
  };

  const handleDeleteFile = () => {
    formChanges.updateState({ headsOfTerms: null });
  };

  const validateLettingPublishParams = useMemo(() => {
    const formatDate = (date: MaterialDate): string => {
      if (!date) return '';
      if (typeof date === 'string') return date;
      return date.format('YYYY-MM-DD');
    };

    const transformedAddress = formChanges.state.existingPropertyId
      ? undefined
      : {
          address_line1: formChanges.state.address.line1,
          address_line2: formChanges.state.address.line2,
          address_name: formChanges.state.address.name,
          address_postcode: formChanges.state.address.postcode,
          address_town: formChanges.state.address.town,
          address_county: formChanges.state.address.county,
          country: formChanges.state.countrySelectValue ? formChanges.state.countrySelectValue.id : '',
        };

    return {
      request_source: 'create_form' as const,
      signature_date: formatDate(formChanges.state.transactionDate),
      building_types: formChanges.state.buildingTypes,
      our_disposal: formChanges.state.ourDisposal,
      our_acquisition: formChanges.state.ourAcquisition,
      disposal_id: null,
      match_id: undefined,
      property_id: formChanges.state.existingPropertyId,
      rent: formChanges.state.rent ? parseFloat(formChanges.state.rent) : undefined,
      rent_currency: formChanges.state.rentCurrency,
      rent_metric: formChanges.state.rentMetric,
      address: transformedAddress,
    };
  }, [
    formChanges.state.transactionDate,
    formChanges.state.buildingTypes,
    formChanges.state.existingPropertyId,
    formChanges.state.address,
    formChanges.state.countrySelectValue,
    formChanges.state.ourDisposal,
    formChanges.state.ourAcquisition,
    formChanges.state.rent,
    formChanges.state.rentCurrency,
    formChanges.state.rentMetric,
  ]);

  const validateSalePublishParams = useMemo(() => {
    const formatDate = (date: MaterialDate): string => {
      if (!date) return '';
      if (typeof date === 'string') return date;
      return date.format('YYYY-MM-DD');
    };

    const transformedAddress = formChanges.state.existingPropertyId
      ? undefined
      : {
          address_line1: formChanges.state.address.line1,
          address_line2: formChanges.state.address.line2,
          address_name: formChanges.state.address.name,
          address_postcode: formChanges.state.address.postcode,
          address_town: formChanges.state.address.town,
          address_county: formChanges.state.address.county,
          country: formChanges.state.countrySelectValue ? formChanges.state.countrySelectValue.id : '',
        };

    return {
      request_source: 'create_form' as const,
      completion_date: formatDate(formChanges.state.transactionDate),
      building_types: formChanges.state.buildingTypes,
      our_disposal: formChanges.state.ourDisposal,
      our_acquisition: formChanges.state.ourAcquisition,
      disposal_id: null,
      match_id: undefined,
      property_id: formChanges.state.existingPropertyId,
      purchase_price: formChanges.state.purchasePrice ? parseFloat(formChanges.state.purchasePrice) : undefined,
      purchase_price_currency: formChanges.state.purchasePriceCurrency,
      address: transformedAddress,
    };
  }, [
    formChanges.state.transactionDate,
    formChanges.state.buildingTypes,
    formChanges.state.existingPropertyId,
    formChanges.state.address,
    formChanges.state.countrySelectValue,
    formChanges.state.ourDisposal,
    formChanges.state.ourAcquisition,
    formChanges.state.purchasePrice,
    formChanges.state.purchasePriceCurrency,
  ]);

  const validateLettingPublishQuery = useValidateDisposalLettingPublishQuery(validateLettingPublishParams, {
    enabled: false,
  });

  const validateSalePublishQuery = useValidateDisposalSalePublishQuery(validateSalePublishParams, {
    enabled: false,
  });

  const saleTransactionsCreateMutation = useSaleTransactionsCreateMutation({
    onSuccess: (data) => {
      if (!data?.id) return;

      const transactionId = data.id.toString();
      setSavedTransactionId(transactionId);

      if (pageState.publish_options === 'true') {
        if (data.publish_to_atlas && !data.publish_to_atlas.published) {
          updatePageState({
            publish_error: t('this_transaction_has_been_saved_but_not_published_to_atlas'),
            publish_options: '',
          });
        } else {
          updatePageState({ publish_success: 'true', publish_error: '', publish_options: '' });
        }
      } else {
        history.push(
          `${ROUTES.organisation.sales_manage.root(transactionId)}${ROUTES.organisation.sales_manage.summary}`
        );
      }
    },
  });

  const lettingTransactionsCreateMutation = useLettingTransactionsCreateMutation({
    onSuccess: (data) => {
      if (!data?.id) return;

      const transactionId = data.id.toString();
      setSavedTransactionId(transactionId);

      if (pageState.publish_options === 'true') {
        if (data.publish_to_atlas && !data.publish_to_atlas.published) {
          updatePageState({
            publish_error: t('this_transaction_has_been_saved_but_not_published_to_atlas'),
            publish_options: '',
          });
        } else {
          updatePageState({ publish_success: 'true', publish_error: '', publish_options: '' });
        }
      } else {
        history.push(
          `${ROUTES.organisation.leases_manage.root(transactionId)}${ROUTES.organisation.leases_manage.summary}`
        );
      }
    },
  });

  const submitFinalData = () => {
    // Building types validation (Atlas only)
    if (hasAtlasPermissions && formChanges.state.buildingTypes.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        buildingTypesError: true,
        error: t('select_property_types_1'),
      }));
      return;
    }

    // Space validation
    const spaceValidationResult = validateSpaces(formChanges.state.floorUnits);
    if (spaceValidationResult.hasErrors) {
      setValidationErrors((prev) => ({
        ...prev,
        spaceCardErrors: spaceValidationResult.errors,
        error: t('please_fix_the_errors_above_and_try_again'),
      }));
      return;
    }

    if (!formChanges.state.sizeValue) {
      formChanges.updateState({ sizeError: true });
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!formChanges.state.assignee) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!formChanges.state.mapPosition) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!isCreateTransactionType(createType)) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_select_a_transaction_type') }));
      return;
    }

    const isLettingTransaction = [
      CreateTransactionTypes.LEASE,
      CreateTransactionTypes.RENT_REVIEW,
      CreateTransactionTypes.LEASE_RENEWAL,
    ].includes(createType);

    const addressData: AddressPayload = {
      address_county: formChanges.state.address.county,
      address_line1: formChanges.state.address.line1,
      address_line2: formChanges.state.address.line2,
      address_name: formChanges.state.address.name,
      address_postcode: formChanges.state.address.postcode,
      address_town: formChanges.state.address.town,
      country: formChanges.state.countrySelectValue ? formChanges.state.countrySelectValue.id : '',
      domestic_id: formChanges.state.address.domestic_id || null,
      map_position_lat: formChanges.state.mapPosition.lat,
      map_position_lng: formChanges.state.mapPosition.lng,
      map_position_zoom: formChanges.state.mapPosition.zoom,
    };

    let ourAcquisition: NumberBoolean = 0;
    let ourDisposal: NumberBoolean = 0;
    if (headlineVisible) {
      ourAcquisition = formChanges.state.ourAcquisition ? 1 : 0;
      ourDisposal = formChanges.state.ourDisposal ? 1 : 0;
    } else {
      ourAcquisition = formChanges.state.ourAcquisition ? 1 : 0;
      ourDisposal = formChanges.state.ourDisposal ? 1 : 0;
    }

    const shouldPublish = pageState.publish_options === 'true';

    if (isLettingTransaction) {
      return lettingTransactionsCreateMutation.mutate({
        body: serialiseLettingTransaction(
          formChanges.state,
          createType as CreateTransactionTypes,
          addressData,
          ourAcquisition,
          ourDisposal,
          shouldPublish,
          fieldVisibility,
          lettingsFieldList
        ),
      });
    } else {
      const salesFieldList = formChanges.state.saleType === 1 ? salesFieldInvestment : salesFieldOccupational;

      return saleTransactionsCreateMutation.mutate({
        body: serialiseSaleTransaction(
          formChanges.state,
          createType as CreateTransactionTypes,
          addressData,
          ourAcquisition,
          ourDisposal,
          shouldPublish,
          fieldVisibility,
          salesFieldList
        ),
      });
    }
  };

  const handleOpenPublishOptions = async () => {
    // Building types validation (Atlas only)
    if (formChanges.state.buildingTypes.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        buildingTypesError: true,
        error: t('select_property_types_1'),
      }));
      return;
    }

    const spaceValidationResult = validateSpaces(formChanges.state.floorUnits);
    if (spaceValidationResult.hasErrors) {
      setValidationErrors((prev) => ({
        ...prev,
        spaceCardErrors: spaceValidationResult.errors,
        error: t('please_fix_the_errors_above_and_try_again'),
      }));
      return;
    }

    if (!formChanges.state.sizeValue) {
      formChanges.updateState({ sizeError: true });
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!formChanges.state.assignee) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!formChanges.state.mapPosition) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_fix_the_errors_above_and_try_again') }));
      return;
    }

    if (!isCreateTransactionType(createType)) {
      setValidationErrors((prev) => ({ ...prev, error: t('please_select_a_transaction_type') }));
      return;
    }

    const isLettingTransaction = [
      CreateTransactionTypes.LEASE,
      CreateTransactionTypes.RENT_REVIEW,
      CreateTransactionTypes.LEASE_RENEWAL,
    ].includes(createType);

    if (!formChanges.state.transactionDate) {
      setValidationErrors((prev) => ({
        ...prev,
        transactionDateError: true,
        error: isLettingTransaction ? t('please_add_transaction_date') : t('please_add_completion_date'),
      }));
      return;
    }

    // Rent/price validation for Atlas publishing
    if (isLettingTransaction && !formChanges.state.rent) {
      setValidationErrors((prev) => ({
        ...prev,
        rentError: true,
        error: t('please_add_headline_rent_to_publish_to_atlas'),
      }));
      return;
    }

    if (!isLettingTransaction && !formChanges.state.purchasePrice) {
      setValidationErrors((prev) => ({
        ...prev,
        purchasePriceError: true,
        error: t('please_add_purchase_price_to_publish_to_atlas'),
      }));
      return;
    }

    try {
      const validateQuery = isLettingTransaction ? validateLettingPublishQuery : validateSalePublishQuery;
      const validationResult = await validateQuery.refetch();

      if (validationResult.isError && validationResult.error) {
        const apiError = validationResult.error as ApiErrorResponse;
        const errorResponseData = apiError.response?.data;
        const statusCode = apiError.response?.status;

        if (statusCode === 500) {
          const serverErrorMessage = t(
            'an_unexpected_error_occurred_on_the_server_our_team_has_been_notified_please_try_again_later_if_the_problem_persists_contact_support'
          );
          setValidationErrors((prev) => ({
            ...prev,
            error: serverErrorMessage,
            atlasValidationErrors: null,
          }));
          return;
        }

        if (errorResponseData) {
          const errors = errorResponseData.errors;

          // Pre-creation validation returns errors as an array directly
          // Saved transaction validation returns { mandatory_errors: [], ranking_errors: [] }
          const isArrayFormat = Array.isArray(errors);
          const errorList = isArrayFormat
            ? (errors as Array<{ field?: string; message?: string }>)
            : ((errors?.mandatory_errors || []) as Array<{ field?: string; message?: string }>);

          let errorMessage = t('please_fix_validation_errors_before_publishing_to_atlas');
          if (errorResponseData.message) {
            errorMessage = errorResponseData.message;
          } else if (errorList.length > 0) {
            errorMessage = errorList[0]?.message || t('please_fix_validation_errors_before_publishing_to_atlas');
          }

          const hasFieldError = (fieldName: string) =>
            errorList.some((err) => err.field === fieldName || err.field?.includes(fieldName));

          const buildingTypesError = !!(errors && !isArrayFormat && errors.building_types) || hasFieldError('building_types');
          const transactionDateError =
            !!(errors && !isArrayFormat && (errors.signature_date || errors.completion_date)) ||
            hasFieldError('signature_date') ||
            hasFieldError('completion_date');
          const ourDisposalError = !!(errors && !isArrayFormat && errors.our_disposal) || hasFieldError('our_disposal');
          const purchasePriceError = !!(errors && !isArrayFormat && errors.purchase_price) || hasFieldError('purchase_price');
          const rentError = !!(errors && !isArrayFormat && errors.rent) || hasFieldError('rent');

          setValidationErrors((prev) => ({
            ...prev,
            error: errorMessage,
            atlasValidationErrors: isArrayFormat
              ? ({ mandatory_errors: errors, ranking_errors: [] } as unknown as AtlasValidationErrorResponse)
              : (errors as unknown as AtlasValidationErrorResponse),
            buildingTypesError,
            ourDisposalError,
            purchasePriceError,
            rentError,
            transactionDateError,
          }));
          return;
        }
      }

      updatePageState({ publish_options: 'true' });
    } catch (err) {
      // Handle axios error response (422 validation errors throw here)
      const apiError = err as ApiErrorResponse;
      const errorResponseData = apiError?.response?.data;

      if (errorResponseData?.errors) {
        const errors = errorResponseData.errors;

        // Pre-creation validation returns errors as an array directly
        // Saved transaction validation returns { mandatory_errors: [], ranking_errors: [] }
        const isArrayFormat = Array.isArray(errors);
        const errorList = isArrayFormat
          ? (errors as Array<{ field?: string; message?: string }>)
          : ((errors?.mandatory_errors || []) as Array<{ field?: string; message?: string }>);

        let errorMessage = t('please_fix_validation_errors_before_publishing_to_atlas');
        if (errorResponseData.message) {
          errorMessage = errorResponseData.message;
        } else if (errorList.length > 0) {
          errorMessage = errorList[0]?.message || t('please_fix_validation_errors_before_publishing_to_atlas');
        }

        const hasFieldError = (fieldName: string) =>
          errorList.some((e) => e.field === fieldName || e.field?.includes(fieldName));

        setValidationErrors((prev) => ({
          ...prev,
          error: errorMessage,
          atlasValidationErrors: isArrayFormat
            ? ({ mandatory_errors: errors, ranking_errors: [] } as unknown as AtlasValidationErrorResponse)
            : (errors as unknown as AtlasValidationErrorResponse),
          buildingTypesError: hasFieldError('building_types'),
          ourDisposalError: hasFieldError('our_disposal'),
          purchasePriceError: hasFieldError('purchase_price'),
          rentError: hasFieldError('rent'),
          transactionDateError: hasFieldError('signature_date') || hasFieldError('completion_date'),
        }));
        return;
      }

      setValidationErrors((prev) => ({
        ...prev,
        error: t('failed_to_validate_transaction_please_try_again'),
        atlasValidationErrors: null,
      }));
    }
  };

  const handleClosePublishSuccess = () => {
    if (savedTransactionId && isCreateTransactionType(createType)) {
      const isLettingTransaction = [
        CreateTransactionTypes.LEASE,
        CreateTransactionTypes.RENT_REVIEW,
        CreateTransactionTypes.LEASE_RENEWAL,
      ].includes(createType);

      if (isLettingTransaction) {
        history.push(
          `${ROUTES.organisation.leases_manage.root(savedTransactionId)}${ROUTES.organisation.leases_manage.summary}`
        );
      } else {
        history.push(
          `${ROUTES.organisation.sales_manage.root(savedTransactionId)}${
            ROUTES.organisation.sales_manage.summary
          }?created=true`
        );
      }
    } else {
      onClose();
    }
  };

  const handleReturnFromError = () => {
    updatePageState({ publish_error: '', publish_options: '' });
  };

  const handleRetryPublish = () => {
    updatePageState({ publish_error: '' });
    submitFinalData();
  };

  const getSaveErrorBodyText = (errorReason?: string) => {
    return t(
      'we_couldnt_save_this_transaction_because_of_xerror_reasonx_you_can_try_again_or_reach_out_to_xsupport_linkx_if_the_issue_continues',
      {
        error_reason: `${'"' + errorReason + '"'}` || t('an_unexpected_error'),
        support_link: `<a href="mailto:hello@kato.app" class="${classes.supportLink}">support</a>`,
      }
    );
  };

  let content: React.ReactNode = '';
  let footerGoBackText = '';
  let footerButton: React.ReactNode = '';
  switch (step) {
    case CreateTransactionSteps.SELECT_TYPE:
      content = (
        <div className={classes.cardsWrapFullWidth}>
          {type === TransactionType.lettings && (
            <>
              <StoreTransactionCard
                header={t('lease')}
                text={t('store_a_new_completed_letting')}
                onClickHandler={() => selectTransactionType(CreateTransactionTypes.LEASE)}
                image={<RequirementDocumentImage />}
              />
              <StoreTransactionCard
                header={t('rent_review')}
                text={t('start_a_rent_review')}
                onClickHandler={() => selectTransactionType(CreateTransactionTypes.RENT_REVIEW)}
                image={<RequirementDocumentImage />}
              />
              <StoreTransactionCard
                header={t('lease_renewal')}
                text={t('start_a_lease_renewal')}
                onClickHandler={() => selectTransactionType(CreateTransactionTypes.LEASE_RENEWAL)}
                image={<RequirementDocumentImage />}
              />
            </>
          )}
          {type === TransactionType.sales && (
            <>
              <StoreTransactionCard
                header={t('occupational_sale')}
                text={t('purchaser_will_be_occupying_the_property')}
                onClickHandler={() => selectTransactionType(CreateTransactionTypes.OCCUPATIONAL_SALE)}
                image={<RequirementDocumentImage />}
              />
              <StoreTransactionCard
                header={t('investment_sale')}
                text={t('purchaser_will_not_be_occupying_the_property')}
                onClickHandler={() => selectTransactionType(CreateTransactionTypes.INVESTMENT_SALE)}
                image={<RequirementDocumentImage />}
              />
            </>
          )}
        </div>
      );
      break;

    case CreateTransactionSteps.ADDRESS_LOOKUP:
      content = (
        <AddressLookup
          search={formChanges.state.searchAddressInput}
          onAddressConfirmStep={() => setStep(CreateTransactionSteps.ADDRESS_CONFIRM)}
          onManuallyAddress={thirdStepHandle}
          onSearchChange={(search) => formChanges.updateState({ searchAddressInput: search })}
        />
      );

      footerGoBackText = t('change_transaction_type');
      break;
    case CreateTransactionSteps.PROPERTIES_LOOKUP:
      content = (
        <PropertiesLookup
          address={formChanges.state.address}
          selectedPropertyId={formChanges.state.existingPropertyId}
          title={t('this_is_your_transaction_address')}
          onSelectPropertyId={(id) => {
            if (formChanges.state.existingPropertyId) {
              formChanges.updateState({
                floorUnits: [],
                propertyDetailsRadio: 'entireProperty',
                // Reset space details
                sizeValue: '',
                sizeMetric: constants.defaultSizeMetric || '',
                sizeMeasure: '',
                // Reset lease details
                leaseEnd: '',
                leaseEndReadable: '',
                leaseLengthDays: '',
                leaseLengthMonths: '',
                leaseLengthYears: '',
                leaseStart: null,
                rent: '',
                rentCurrency: constants.localisation.currencyDefaultIso ?? '',
                rentMetric: constants.defaultRentMetric || '',
                rentRates: 'exclusive',
                transactionDate: moment().format('YYYY-MM-DD'),
                transactionType: 0,
                assignee: configUserToLookupUser(user),
              });
            }

            formChanges.updateState({ existingPropertyId: id });
          }}
          onSkipStep={handleSkipPropertyLookupStep}
        />
      );

      footerGoBackText = t('back_to_search');

      footerButton = (
        <Button classes={{ root: classes.bottomSubmitBtn }} text={t('continue')} onClick={fourthStepHandle} />
      );

      break;

    case CreateTransactionSteps.ADDRESS_CONFIRM:
      content = (
        <div className={classes.cardsWrap}>
          <Card elevation={0} classes={{ root: classes.card }}>
            <Flex justifyContent="space-between" className={classes.thirdStepHeader}>
              <span className={classes.headerTitle}>{t('property_details')}</span>
              <Button
                colour={NewButtonColour.Outline}
                onClick={returnToPreviousStep}
                text={t('back_to_search')}
                variant="outlined"
              />
            </Flex>
            <TextField
              className={classes.inputField}
              fullWidth
              warning={!!validationErrors.error && !!validationErrors.addressFormErrors.nameWarning}
              warningText={validationErrors.addressFormErrors.nameWarning}
              label={t('building_or_unit_name_bracket_if_not_specified_in_address_bracket')}
              infoText={t('this_field_is_for_the_building_name_units')}
              onChange={(e) =>
                formChanges.updateState({
                  address: { ...formChanges.state.address, name: e.target.value || '' },
                })
              }
              placeholder={t('xtextx_ellipsis', { text: t('building_name') })}
              value={formChanges.state.address.name}
              variant="outlined"
            />
            <TextField
              className={classes.inputField}
              disabled={!formChanges.state.enterManuallyMode}
              error={!!validationErrors.error && !!validationErrors.addressFormErrors.line1}
              errorText={validationErrors.addressFormErrors.line1}
              warning={!!validationErrors.error && !!validationErrors.addressFormErrors.line1Warning}
              warningText={validationErrors.addressFormErrors.line1Warning}
              fullWidth
              label={t('xtextx_asterisk', { text: t('address_line_1') })}
              onChange={(e) =>
                formChanges.updateState({
                  address: { ...formChanges.state.address, line1: e.target.value || '' },
                })
              }
              placeholder={t('xtextx_ellipsis', { text: t('address_line_1') })}
              value={formChanges.state.address.line1}
              variant="outlined"
            />
            <TextField
              className={classes.inputField}
              disabled={!formChanges.state.enterManuallyMode}
              fullWidth
              label={t('address_line_2')}
              onChange={(e) =>
                formChanges.updateState({
                  address: { ...formChanges.state.address, line2: e.target.value || '' },
                })
              }
              placeholder={t('xtextx_ellipsis', { text: t('address_line_2') })}
              value={formChanges.state.address.line2}
            />
            <div className={classes.inputsGroup}>
              <TextField
                className={classes.groupedInput}
                disabled={!formChanges.state.enterManuallyMode}
                fullWidth
                label={t('town_or_city')}
                onChange={(e) =>
                  formChanges.updateState({
                    address: { ...formChanges.state.address, town: e.target.value || '' },
                  })
                }
                placeholder={t('xtextx_ellipsis', { text: t('post_town_or_city') })}
                value={formChanges.state.address.town}
              />
              <TextField
                className={classes.groupedInput}
                disabled={!formChanges.state.enterManuallyMode}
                fullWidth
                label={t('county')}
                onChange={(e) =>
                  formChanges.updateState({
                    address: { ...formChanges.state.address, county: e.target.value || '' },
                  })
                }
                placeholder={t('xtextx_ellipsis', { text: t('county') })}
                value={formChanges.state.address.county}
              />
            </div>
            <div className={classes.inputsGroup}>
              <TextField
                className={classes.groupedInput}
                disabled={!formChanges.state.enterManuallyMode}
                error={!!validationErrors.error && !!validationErrors.addressFormErrors.postcode}
                errorText={validationErrors.addressFormErrors.postcode}
                fullWidth
                label={isUkRegion ? t('xtextx_asterisk', { text: t('postcode') }) : t('postcode')}
                onChange={(e) =>
                  formChanges.updateState({
                    address: { ...formChanges.state.address, postcode: e.target.value || '' },
                  })
                }
                placeholder={t('xtextx_ellipsis', { text: t('postcode') })}
                value={formChanges.state.address.postcode}
              />
              <Flex autoWidth={false} direction="column" classes={{ root: classes.filter }}>
                <span className={classes.label}>{t('xtextx_asterisk', { text: t('country') })}</span>
                <SingleSelect
                  classes={{ root: classes.groupedSelect }}
                  disabled={!formChanges.state.enterManuallyMode}
                  onChange={handleSelectCountry}
                  options={countryOptions}
                  placeholder={t('please_select')}
                  value={formChanges.state.countrySelectValue?.id}
                />
              </Flex>
            </div>
            <div className={classes.inputsGroup}>
              {tagGroupOptions?.length > 0 && (
                <MultiGroupSelect
                  classes={{ root: classes.groupedInput, label: classes.groupedInputLabel }}
                  fullWidth
                  placeholder={t('please_select')}
                  groupOptions={tagGroupOptions}
                  infoText={t('reach_out_to_support_for_additional_tag_options')}
                  label={t('tags')}
                  value={formChanges.state.selectedTagIds}
                  onChange={(ids) => formChanges.updateState({ selectedTagIds: ids as number[] })}
                />
              )}
              {isUprnEditable && (
                <TextField
                  className={classes.groupedInput}
                  disabled={!formChanges.state.enterManuallyMode}
                  fullWidth
                  infoText={showInternalUseText ? t('only_visible_for_kato_admins') : ''}
                  label={t('uprn')}
                  onChange={(e) =>
                    formChanges.updateState({
                      address: { ...formChanges.state.address, domestic_id: e.target.value || '' },
                    })
                  }
                  placeholder={t('uprn_number')}
                  value={formChanges.state.address.domestic_id}
                />
              )}
            </div>
            {!formChanges.state.enterManuallyMode && (
              <div
                className={classes.stepHelpText}
                onClick={() => formChanges.updateState({ enterManuallyMode: true })}
              >
                {t('trouble_with_the_address_question_enter_manually')}
              </div>
            )}
            {formChanges.state.enterManuallyMode && (
              <div className={classes.bottomButtonWrap}>
                <Button
                  colour={NewButtonColour.PrimaryNavy}
                  disabled={!formChanges.state.address.postcode}
                  onClick={() => getCoords(formChanges.state.address)}
                  text={t('locate_on_map')}
                  tooltip={
                    !formChanges.state.address.postcode ? t('xtextx_is_required', { text: t('postcode') }) : undefined
                  }
                  tooltipPlacement="left"
                  variant="outlined"
                />
              </div>
            )}
            <div style={{ height: 200 }}>
              {coordsError && <div className={classes.mapLocateError}>{coordsError}</div>}
              <MapboxMap
                initialMapPosition={formChanges.state.mapPosition}
                loading={!formChanges.state.mapPosition || coordsLoading}
                ref={mapRef}
                onMapClick={(e) =>
                  formChanges.updateState({
                    mapPosition: formChanges.state.mapPosition && {
                      ...formChanges.state.mapPosition,
                      lat: e.lngLat.lat,
                      lng: e.lngLat.lng,
                    },
                  })
                }
                onMapLoad={() => centreMap()}
                onMapZoom={(e) =>
                  formChanges.updateState({
                    mapPosition: formChanges.state.mapPosition && {
                      ...formChanges.state.mapPosition,
                      zoom: e.viewState.zoom,
                    },
                  })
                }
              >
                {typeof formChanges.state.mapPosition?.lng === 'number' &&
                  typeof formChanges.state.mapPosition?.lat === 'number' &&
                  !isNaN(formChanges.state.mapPosition.lng) &&
                  !isNaN(formChanges.state.mapPosition.lat) && (
                    <Marker
                      longitude={formChanges.state.mapPosition.lng}
                      latitude={formChanges.state.mapPosition.lat}
                      draggable
                      onDragEnd={(e) =>
                        formChanges.updateState({
                          mapPosition: formChanges.state.mapPosition && {
                            ...formChanges.state.mapPosition,
                            lat: e.lngLat.lat,
                            lng: e.lngLat.lng,
                          },
                        })
                      }
                    >
                      <IconButton disableRipple classes={{ root: classes.markerIconButton }}>
                        <MarkerIcon classes={{ root: classes.markerIcon }} />
                      </IconButton>
                    </Marker>
                  )}
              </MapboxMap>
            </div>
          </Card>
        </div>
      );

      footerGoBackText = t('back_to_search');
      footerButton = (
        <Button
          text={t('save_and_set_space_details')}
          colour={NewButtonColour.PrimaryNavy}
          onClick={handleSaveAndSetSpaceDetails}
          classes={{ root: classes.bottomSubmitBtn }}
        />
      );
      break;

    case CreateTransactionSteps.SET_DETAILS:
      content = (
        <div className={classes.cardsWrap}>
          <Card elevation={0} classes={{ root: classes.card }}>
            <RecordSpacesTakenForm
              buildingTypes={formChanges.state.buildingTypes}
              buildingTypesError={validationErrors.buildingTypesError}
              entireProperty={formChanges.state.entireProperty}
              fitoutConditionOptions={fitoutConditionOptions}
              floorUnits={formChanges.state.floorUnits}
              ourDisposal={formChanges.state.ourDisposal}
              sizeMeasure={formChanges.state.sizeMeasure}
              propertyAddress={formattedFullPropertyAddress}
              propertyId={formChanges.state.existingPropertyId || 0}
              sizeError={formChanges.state.sizeError}
              sizeMetric={formChanges.state.sizeMetric}
              sizeValue={formChanges.state.sizeValue}
              spaceCardErrors={validationErrors.spaceCardErrors}
              onAddFloorUnit={() => {
                const newFloorUnit: DisposalRecordFloorUnit = {
                  fitout_condition: '',
                  floor_string: '',
                  floor_unit_id: null,
                  floor: '',
                  id: null,
                  include: 1 as NumberBoolean,
                  part_floor: false,
                  property_id: formChanges.state.existingPropertyId || 0,
                  property_types: [],
                  size_metric: constants.defaultSizeMetric,
                  size_value_sqft: null,
                  size_value: '',
                  status: '',
                  unit: '',
                  unit_string: '',
                };
                formChanges.updateState({
                  floorUnits: [...formChanges.state.floorUnits, newFloorUnit],
                });
              }}
              onChangeBuildingTypes={(types) => {
                formChanges.updateState({ buildingTypes: types });
                setValidationErrors((prev) => ({ ...prev, buildingTypesError: false }));
              }}
              onChangeEntireProperty={(value: number) => {
                formChanges.updateState({
                  entireProperty: value,
                  floorUnits: formChanges.state.floorUnits.map((unit) => ({
                    ...unit,
                    include: value as NumberBoolean,
                  })),
                });
              }}
              onChangeMeasureType={(sizeMeasure) => {
                formChanges.updateState({ sizeMeasure });
              }}
              onChangeSizeMetric={(metric) => {
                // Update all floor units to use the new metric
                const updatedFloorUnits = formChanges.state.floorUnits.map((unit) => ({
                  ...unit,
                  size_metric: metric,
                }));
                formChanges.updateState({
                  sizeMetric: metric,
                  floorUnits: updatedFloorUnits,
                });
              }}
              onChangeSizeValue={(value) => {
                formChanges.updateState({
                  sizeValue: value,
                  sizeError: false,
                });
              }}
              onEditAddress={returnToPreviousStep}
              onEditFloorUnit={(update, idx) => {
                const updatedFloorUnits = [...formChanges.state.floorUnits];
                updatedFloorUnits[idx] = { ...updatedFloorUnits[idx]!, ...update };
                formChanges.updateState({ floorUnits: updatedFloorUnits });
              }}
            />
          </Card>
          <Card elevation={0} classes={{ root: classes.card }}>
            <Flex justifyContent="flex-start" className={classes.cardHeader}>
              <span className={classes.cardTitle}>
                {createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                createType === CreateTransactionTypes.INVESTMENT_SALE
                  ? t('purchaser_and_vendor')
                  : t('tenant_and_landlord')}
              </span>
            </Flex>
            <div className={classes.subHeaderLabel}>
              {createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
              createType === CreateTransactionTypes.INVESTMENT_SALE
                ? t('purchaser')
                : t('tenant')}
            </div>

            <CompanySingleLookup
              inputProps={{ size: InputSize.medium }}
              placeholder={t('search_or_create_a_company_record')}
              value={formChanges.state.company}
              onCreate={(newCompany) => {
                setCreateCompanyField('company');
                setCreateCompanyName(newCompany.name);
              }}
              onRemove={() => formChanges.updateState({ company: undefined })}
              onSelect={(company) => formChanges.updateState({ company })}
            />

            <>
              <div className={classes.divider} />
              <span className={classes.subHeaderLabel}>
                {createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                createType === CreateTransactionTypes.INVESTMENT_SALE
                  ? t('vendor')
                  : t('landlord')}
              </span>

              <CompanySingleLookup
                inputProps={{ size: InputSize.medium }}
                placeholder={t('search_or_create_a_company_record')}
                value={
                  createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                  createType === CreateTransactionTypes.INVESTMENT_SALE
                    ? formChanges.state.vendor
                    : formChanges.state.landlord
                }
                onCreate={(newCompany) => {
                  setCreateCompanyField(
                    createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                      createType === CreateTransactionTypes.INVESTMENT_SALE
                      ? 'vendor'
                      : 'landlord'
                  );
                  setCreateCompanyName(newCompany.name);
                }}
                onRemove={() =>
                  createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                  createType === CreateTransactionTypes.INVESTMENT_SALE
                    ? formChanges.updateState({ vendor: undefined })
                    : formChanges.updateState({ landlord: undefined })
                }
                onSelect={(company) =>
                  createType === CreateTransactionTypes.OCCUPATIONAL_SALE ||
                  createType === CreateTransactionTypes.INVESTMENT_SALE
                    ? formChanges.updateState({ vendor: company })
                    : formChanges.updateState({ landlord: company })
                }
              />
            </>
          </Card>
          {headlineVisible ? (
            <Card elevation={0} classes={{ root: classes.card }}>
              <SaleDetailsForm
                data={{
                  assignee: formChanges.state.assignee,
                  capital: formChanges.state.capital,
                  capitalCurrency: formChanges.state.capitalCurrency,
                  date: formChanges.state.transactionDate,
                  investment: createType === CreateTransactionTypes.INVESTMENT_SALE ? 1 : 0,
                  leaseComment: formChanges.state.leaseComment,
                  leaseExpiryMonth: formChanges.state.leaseExpiryMonth?.toString() ?? '',
                  leaseExpiryYear: formChanges.state.leaseExpiryYear,
                  netInitialYield: formChanges.state.netInitialYield,
                  passingRent: formChanges.state.passingRent,
                  passingRentCurrency: formChanges.state.passingRentCurrency,
                  passingRentMetric: formChanges.state.passingRentMetric,
                  purchasePrice: formChanges.state.purchasePrice,
                  purchasePriceCurrency: formChanges.state.purchasePriceCurrency,
                  saleType:
                    typeof formChanges.state.saleType === 'number'
                      ? formChanges.state.saleType
                        ? formChanges.state.saleType.toString()
                        : ''
                      : formChanges.state.saleType,
                  sectors: formChanges.state.sectors,
                  source: formChanges.state.source,
                  waultToBreak: formChanges.state.waultToBreak,
                  waultToExpiry: formChanges.state.waultToExpiry,
                }}
                errors={{ date: validationErrors.transactionDateError, purchasePrice: validationErrors.purchasePriceError }}
                notInSchedule={formChanges.state.tenantRadio === '1'}
                updateData={(data) => {
                  const updates: Partial<CreateTransactionFormData> = {
                    ...(data as Record<string, unknown>),
                  };

                  if (data.leaseExpiryMonth !== undefined) {
                    updates.leaseExpiryMonth = Number(data.leaseExpiryMonth);
                  }
                  if (data.saleType !== undefined) {
                    updates.saleType = Number(data.saleType);
                  }

                  if (data.date !== undefined) {
                    updates.transactionDate = data.date;
                    delete (updates as Record<string, unknown>).date;
                  }

                  formChanges.updateState(updates);
                }}
              />
            </Card>
          ) : (
            <Card elevation={0} classes={{ root: classes.card }}>
              <LettingDetailsForm
                data={{
                  assignee: formChanges.state.assignee,
                  leaseEnd: formChanges.state.leaseEnd,
                  leaseEndReadable: formChanges.state.leaseEndReadable || '',
                  leaseLengthDays: formChanges.state.leaseLengthDays,
                  leaseLengthMonths: formChanges.state.leaseLengthMonths,
                  leaseLengthYears: formChanges.state.leaseLengthYears,
                  leaseStart: formChanges.state.leaseStart,
                  sectors: formChanges.state.sectors,
                  source: formChanges.state.source,
                  rent: formChanges.state.rent,
                  rentCurrency: formChanges.state.rentCurrency,
                  rentMetric: formChanges.state.rentMetric,
                  rentRates: formChanges.state.rentRates,
                  transactionDate: formChanges.state.transactionDate,
                  transactionType: formChanges.state.transactionType,
                }}
                errors={{ rent: validationErrors.rentError, transactionDate: validationErrors.transactionDateError }}
                lettingTypeOptions={availableLettingTypeOptions}
                notInSchedule={formChanges.state.tenantRadio === '1'}
                updateData={(data) => {
                  const updates: Partial<CreateTransactionFormData> = {};

                  (Object.keys(data) as Array<keyof LettingDetailsFormData>).forEach((key) => {
                    if (key === 'transactionType' && data.transactionType !== undefined) {
                      updates.transactionType = Number(data.transactionType);
                    } else {
                      (updates as Record<string, unknown>)[key as string] = data[key];
                    }
                  });

                  formChanges.updateState(updates);
                }}
              />
            </Card>
          )}
          <AgencyRoleSection
            transactionType={type}
            ourDisposal={formChanges.state.ourDisposal}
            ourAcquisition={formChanges.state.ourAcquisition}
            ourDisposalError={validationErrors.ourDisposalError}
            landlordAgents={formChanges.state.landlordAgents}
            tenantAgents={formChanges.state.tenantAgents}
            onOurDisposalChange={(checked) => formChanges.updateState({ ourDisposal: checked })}
            onOurAcquisitionChange={(checked) => formChanges.updateState({ ourAcquisition: checked })}
            onLandlordAgentsChange={(agents) => formChanges.updateState({ landlordAgents: agents })}
            onTenantAgentsChange={(agents) => formChanges.updateState({ tenantAgents: agents })}
          />
          {!headlineVisible && (
            <ManageSection
              classes={{
                root: classes.manageSection,
              }}
              title={t('incentives_and_events')}
              collapsed={!incentivesExpanded}
              disableShadow={true}
              divider={false}
              onChangeCollapsed={(collapsed) => setIncentivesExpanded(!collapsed)}
            >
              <div className={classes.subHeaderLabel}>{t('rent_free_periods')}</div>
              {formChanges.state.rentFreeEvents.length > 0 && (
                <div className={classes.eventsContainer}>
                  {formChanges.state.rentFreeEvents.map((event, index) => (
                    <Fragment key={event.id}>
                      <DisposalRentEventCard
                        event={event}
                        error={validationErrors.rentFreeEventsErrors[event.id.toString()] ?? false}
                        onUpdate={handleUpdateRentEvent}
                        onDelete={handleDeleteRentEvent}
                      />
                      {index < formChanges.state.rentFreeEvents.length - 1 && (
                        <div className={classes.sectionDivider} />
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
              <Button
                startIcon={<PlusIcon />}
                size={ButtonSize.small}
                text={t('add_rent_free_period')}
                onClick={handleAddRentEvent}
                className={classes.addEventButton}
              />
              <div className={classes.sectionDivider} />
              <div className={classes.subHeaderLabel}>{t('lease_breaks')}</div>
              {formChanges.state.leaseBreaks.length > 0 && (
                <div className={classes.eventsContainer}>
                  {formChanges.state.leaseBreaks.map((leaseBreak, index) => (
                    <Fragment key={leaseBreak.id}>
                      <DisposalLeaseBreakCard
                        currency={formChanges.state.rentCurrency}
                        item={leaseBreak}
                        error={validationErrors.leaseBreaksErrors[leaseBreak.id?.toString() ?? ''] ?? false}
                        onUpdate={(updates) => handleUpdateLeaseBreak(leaseBreak.id, updates)}
                        onDelete={() => handleDeleteLeaseBreak(leaseBreak.id)}
                      />
                      {index < formChanges.state.leaseBreaks.length - 1 && <div className={classes.sectionDivider} />}
                    </Fragment>
                  ))}
                </div>
              )}
              <Button
                startIcon={<PlusIcon />}
                size={ButtonSize.small}
                text={t('add_break')}
                onClick={handleAddLeaseBreak}
                className={classes.addEventButton}
              />

              <div className={classes.sectionDivider} />
              <div className={classes.subHeaderLabel}>{t('rent_reviews')}</div>
              {formChanges.state.rentReviews.length > 0 && (
                <div className={classes.eventsContainer}>
                  {formChanges.state.rentReviews.map((rentReview, index) => (
                    <Fragment key={rentReview.id}>
                      <DisposalRentReviewCard
                        item={rentReview}
                        leaseStartDate={formChanges.state.leaseStart}
                        error={validationErrors.rentReviewsErrors[rentReview.id?.toString() ?? ''] ?? false}
                        onUpdate={(updates) => handleUpdateRentReview(rentReview.id, updates)}
                        onDelete={() => handleDeleteRentReview(rentReview.id)}
                      />
                      {index < formChanges.state.rentReviews.length - 1 && <div className={classes.sectionDivider} />}
                    </Fragment>
                  ))}
                </div>
              )}
              <Button
                startIcon={<PlusIcon />}
                size={ButtonSize.small}
                text={t('add_rent_review')}
                onClick={handleAddRentReview}
                className={classes.addEventButton}
              />

              <div className={classes.sectionDivider} />
              <div className={classes.subHeaderLabel}>{t('capital_incentive')}</div>
              {formChanges.state.capitalIncentives.length > 0 && (
                <div className={classes.eventsContainer}>
                  {formChanges.state.capitalIncentives.map((incentive, index) => (
                    <Fragment key={index}>
                      <DisposalCapitalIncentiveCard
                        currency={formChanges.state.rentCurrency}
                        index={index}
                        item={incentive}
                        onUpdate={handleUpdateCapitalIncentive}
                        onDelete={handleDeleteCapitalIncentive}
                      />
                      {index < formChanges.state.capitalIncentives.length - 1 && (
                        <div className={classes.sectionDivider} />
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
              <Button
                startIcon={<PlusIcon />}
                size={ButtonSize.small}
                text={t('add_capital_incentive')}
                onClick={handleAddCapitalIncentive}
                className={classes.addEventButton}
              />
            </ManageSection>
          )}
          <ManageSection
            classes={{
              root: classes.manageSection,
            }}
            title={t('additional_information')}
            collapsed={!additionalInfoExpanded}
            disableShadow={true}
            divider={false}
            onChangeCollapsed={(collapsed) => setAdditionalInfoExpanded(!collapsed)}
          >
            <div className={classes.subHeaderLabel}>{t('heads_of_terms')}</div>
            <DisposalHeadsOfTermsUpload
              file={formChanges.state.headsOfTerms}
              onFileSelect={handleUploadFile}
              onFileDelete={handleDeleteFile}
            />
            <div className={classes.sectionDivider} />
            <div className={classes.subHeaderLabel}>{t('transaction_notes')}</div>
            <TextField
              multiline
              fullWidth
              placeholder={t('add_your_notes_here')}
              minRows={7}
              value={formChanges.state.transactionNotes}
              onChange={(e) => formChanges.updateState({ transactionNotes: e.target.value })}
              classes={{ root: classes.transactionNotesField }}
            />
          </ManageSection>
        </div>
      );

      footerGoBackText = t('back');
      footerButton =
        hasAtlasPermissions &&
        (formChanges.state.address.country === 'United Kingdom' || formChanges.state.address.country === 'GB') ? (
          <div className={classes.footerButtons}>
            <Button
              text={t('save_transaction_to_kato')}
              colour={NewButtonColour.Outline}
              loading={lettingTransactionsCreateMutation.isLoading || saleTransactionsCreateMutation.isLoading}
              onClick={submitFinalData}
            />
            <Button
              text={t('save_and_continue_to_atlas_publishing')}
              colour={NewButtonColour.PrimaryNavy}
              disabled={lettingTransactionsCreateMutation.isLoading || saleTransactionsCreateMutation.isLoading}
              onClick={handleOpenPublishOptions}
            />
          </div>
        ) : (
          <Button
            text={t('store_transaction')}
            colour={NewButtonColour.PrimaryNavy}
            loading={lettingTransactionsCreateMutation.isLoading || saleTransactionsCreateMutation.isLoading}
            onClick={submitFinalData}
            classes={{ root: classes.bottomSubmitBtn }}
          />
        );
      break;
  }

  const renderPublishFlowContent = () => {
    if (pageState.publish_error === t('this_transaction_has_been_saved_but_not_published_to_atlas')) {
      return (
        <AtlasPublishError
          bodyText={t('this_transaction_has_been_saved_but_not_published_to_atlas')}
          headerText={t('transaction_saved_but_not_published')}
          primaryButtonText={t('view_transaction')}
          onPrimaryBtnClick={handleClosePublishSuccess}
        />
      );
    }

    if (pageState.publish_error) {
      return (
        <AtlasPublishError
          bodyText={getSaveErrorBodyText(pageState.publish_error)}
          headerText={t('unable_to_save_transaction')}
          primaryButtonText={t('return_to_xscreen_namex', { screen_name: t('transaction_form') })}
          secondaryButtonText={t('try_again')}
          onPrimaryBtnClick={handleReturnFromError}
          onSecondaryBtnClick={handleRetryPublish}
        />
      );
    }

    if (pageState.publish_success === 'true') {
      return (
        <AtlasPublishSuccess
          bodyText=" " // Intentionally left blank with SPACE to hide body text area
          buttonText={t('view_transaction')}
          headerText={t('transaction_saved_and_successfully_published')}
          onBtnClick={handleClosePublishSuccess}
        />
      );
    }

    if (pageState.publish_options === 'true') {
      return (
        <>
          <DrawerHeader
            header={t('publish_to_atlas')}
            onClose={onClose}
            classes={{
              header: classes.header,
            }}
          />
          <div className={classes.content} ref={containerRef}>
            <AtlasPublishOptionsPanel
              transaction={(() => {
                const formatDateValue = (dateValue: MaterialDate): string | null => {
                  if (!dateValue) return null;
                  if (typeof dateValue === 'string') return dateValue;
                  return dateValue.format('YYYY-MM-DD');
                };

                const commonFields = {
                  id: 0,
                  primary_address: formChanges.state.address.line1,
                  secondary_address: [formChanges.state.address.town, formChanges.state.address.postcode]
                    .filter(Boolean)
                    .join(', '),
                  images: [],
                  size_value: Number(formChanges.state.sizeValue) || 0,
                  size_metric: formChanges.state.sizeMetric,
                  building_types: formChanges.state.buildingTypes || [],
                };

                if (type === TransactionType.lettings) {
                  const rentValue = formChanges.state.rent ? parseFloat(formChanges.state.rent) : null;
                  const previewData: AtlasPublishPreviewLetting = {
                    ...commonFields,
                    rent_currency: formChanges.state.rentCurrency,
                    rent_pa: rentValue,
                    rent_psf: null,
                    lease_start: formatDateValue(formChanges.state.leaseStart),
                    lease_end: formatDateValue(formChanges.state.leaseEnd),
                    signature_date: formatDateValue(formChanges.state.transactionDate),
                  };
                  return previewData as unknown as LettingTransactionsShowItem;
                }

                const previewData: AtlasPublishPreviewSale = {
                  ...commonFields,
                  purchase_price: Number(formChanges.state.purchasePrice) || 0,
                  purchase_price_currency: formChanges.state.purchasePriceCurrency,
                  completion_date: formatDateValue(formChanges.state.transactionDate),
                  passing_rent: formChanges.state.passingRent ? Number(formChanges.state.passingRent) : null,
                  passing_rent_currency:
                    formChanges.state.passingRentCurrency || formChanges.state.purchasePriceCurrency,
                  passing_rent_metric: formChanges.state.passingRentMetric || null,
                  passing_niy: formChanges.state.netInitialYield ? Number(formChanges.state.netInitialYield) : 0,
                  wault_to_break: formChanges.state.waultToBreak ? Number(formChanges.state.waultToBreak) : 0,
                  wault_to_expiry: formChanges.state.waultToExpiry ? Number(formChanges.state.waultToExpiry) : 0,
                };
                return previewData as unknown as SaleTransactionsShowItem;
              })()}
              fieldList={
                type === TransactionType.lettings
                  ? lettingsFieldList
                  : formChanges.state.saleType === 1
                  ? salesFieldInvestment
                  : salesFieldOccupational
              }
              fieldVisibility={fieldVisibility}
              onFieldVisibilityChange={setFieldVisibility}
            />
          </div>
          <CreateNewDrawerFooter
            text={t('back')}
            onBackClick={() => updatePageState({ publish_options: '' })}
            validationErrorText=""
            button={
              <Button
                colour={NewButtonColour.PrimaryNavy}
                loading={lettingTransactionsCreateMutation.isLoading || saleTransactionsCreateMutation.isLoading}
                text={t('save_transaction_and_publish_to_atlas')}
                onClick={submitFinalData}
              />
            }
          />
        </>
      );
    }

    return null;
  };

  const publishFlowContent = renderPublishFlowContent();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      transitionDuration={drawerTransition}
      classes={{ paper: classes.paper }}
    >
      {publishFlowContent || (
        <>
          <DrawerHeader
            header={headerTitle}
            onClose={onClose}
            classes={{
              header: classes.header,
            }}
          />
          <div className={classes.content} ref={containerRef}>
            {content}
          </div>
          {step !== CreateTransactionSteps.SELECT_TYPE && (
            <CreateNewDrawerFooter
              text={footerGoBackText}
              onBackClick={returnToPreviousStep}
              validationErrorText={validationErrors.error}
              button={footerButton}
            />
          )}
        </>
      )}
      {createCompanyName && (
        <CreateUpdateCompanyDrawer
          open={true}
          initialData={{ name: createCompanyName }}
          onClose={() => setCreateCompanyName(null)}
          onCreated={(company) => {
            if (createCompanyField === 'company') {
              formChanges.updateState({ company });
            } else if (createCompanyField === 'vendor') {
              formChanges.updateState({ vendor: company });
            } else {
              formChanges.updateState({ landlord: company });
            }
            setCreateCompanyName(null);
          }}
          onOpenDetails={() => {
            // No-op: We stay in the transaction drawer, not navigating to company details
          }}
        />
      )}
    </Drawer>
  );
};

export const CreateTransactionDrawer = withStyles(styles)(CreateTransactionDrawerComponent);
